<template>
    <cp-base-page
        :page-title="pageTitle"
        :category-subtitle="categorySubtitle"
    >
        <template #search-top>
            <cp-search :search="search"></cp-search>
        </template>

        <template #category-title>
            <v-row align-content="space-around">
                <v-col>
                    <div
                        class="t-heading-medium-lg"
                        v-text="listingTitle"
                    ></div>
                </v-col>
                <v-col class="fill-height d-flex justify-end align-center">
                    <div class="mr-5" v-if="can('filter clients')">
                        <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                                <cp-icon-button
                                    class="ml-3"
                                    icon="$filterOutline"
                                    :color="isFilterOpen ? 'primary' : 'secondary'"
                                    @click="isFilterOpen = !isFilterOpen"
                                    v-bind="attrs"
                                    v-on="on"
                                ></cp-icon-button>
                            </template>
                            <span>Filtros</span>
                        </v-tooltip>
                    </div>
                    <div class="mr-5" v-if="can('export clients')">
                        <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                                <cp-icon-button
                                    class="ml-3"
                                    icon="$download"
                                    color="secondary"
                                    @click="exportClients"
                                    v-bind="attrs"
                                    v-on="on"
                                ></cp-icon-button>
                            </template>
                            <span>Exportar</span>
                        </v-tooltip>
                    </div>
                </v-col>
            </v-row>
        </template>

        <template #filters-top>
            <cp-active-filters-alert :filters="filters"/>
            <v-sheet
                v-if="isFilterOpen"
                outlined
                class="mt-3 pa-4"
            >
                <FilterClients
                    :filters="filters"
                    :search="search"
                />
            </v-sheet>
        </template>

        <template>
            <v-data-table
                :style="{width: '100%' }"
                :headers="headers"
                :items="clients"
                item-key="id"
                mobile-breakpoint="700"
                no-data-text="Sem dados disponíveis"
                hide-default-footer
                :items-per-page="15"
            >
                <template v-slot:item.company="{ item }">
                    <cp-clients :client="item" />
                </template>
                <template v-slot:item.account="{ item }">
                    <v-chip
                        :color="item.statusColor"
                        text-color="white"
                        label
                        small
                        class="mb-2"
                    >
                        {{ item.status }}
                    </v-chip><br>

                    <v-chip
                        :color="item.situationColor"
                        text-color="white"
                        label
                        small
                        class="mb-2"
                    >
                        {{ item.situation }}
                    </v-chip><br>

                    <v-chip
                        :color="item.partnerColor"
                        :text-color="item.partnerTextColor"
                        label
                        small
                        class="mb-2"
                    >
                        {{ item.partner }}
                    </v-chip><br>

                    <v-chip
                        v-if="item.internal_account"
                        color="grey darken-1"
                        text-color="white"
                        label
                        small
                        class="mb-2"
                    >
                        Conta Interna
                    </v-chip>
                    <br v-if="item.internal_account">

                    <v-chip v-if="item.holding_name"
                        color="lime darken-3"
                        text-color="white"
                        label
                        small
                        class="mb-2"
                    >
                        Holding: {{ item.holding_name }}
                    </v-chip>
                </template>
                <template v-slot:item.details="{ item }">
                    <slot name="actions" :item="item">
                        <cp-button color="blue darken-1" :href="route('v2.clients.bills.index', item.id)">Faturas</cp-button>
                    </slot>
                </template>

                <template #footer>
                    <cp-pagination
                        v-if="pagination.last_page > 1"
                        :params="paginationParams"
                        :pagination="pagination"
                    />
                </template>
            </v-data-table>
        </template>
    </cp-base-page>
</template>

<script>
import Client from '@cp-models-common/Client'
import FilterClients from '@core-components/filters/FilterClients'

export default {
    props: {
        pageTitle: {
            type: String,
            required: true
        },
        categorySubtitle: {
            type: String,
            required: true
        },
        listingTitle: {
            type: String,
            required: true
        },
        breadcrumbItems: {
            type: Array,
            required: true
        },
        clientsData: {
            type: [Array, Object],
            default: null
        },
        searchData: {
            type: String,
            default: null
        },
        filtersData: {
            type: Object,
            default: () => ({})
        },
        paginationData: {
            type: Object,
            default: () => ({})
        }
    },

    data: () => ({
        clients: [],
        headers: [
            {text: 'Empresa', value: 'company', sortable: false},
            {text: 'Conta', value: 'account', sortable: false},
            {text: 'Mais informações', value: 'details', sortable: false},
        ],
        search: null,
        filters: {},
        pagination: {},
        isFilterOpen: false,
    }),

    metaInfo() {
        return {
            title: this.pageTitle
        }
    },

    created() {
        this.init();
    },

    components: {
        FilterClients
    },

    methods: {
        init() {
            this.setBreadcrumb(this.breadcrumbItems)

            this.clients = this.clientsData
                ? this.clientsData.map(client => new Client(client))
                : []

            this.search = this.searchData
            this.filters = this.filtersData
            this.pagination = this.paginationData
        },

        exportClients() {
            let data = { filters: this.filters }
            data.search = this.search

            axios.get(this.route('v2.clients.export', data))
            .then(() => {
                this.$bus.$emit('simple-modal', {
                    title: 'Exportando...',
                    message: 'Estamos processando seu relatório, ele será encaminhado para seu e-mail em breve.',
                    btnError: {hide: true},
                    btnSuccess: {color: 'primary', text: 'OK'},
                    persistent: true
                })

            }).catch((error) => {
                let message = error.response.status == 422
                    ? error.response.data
                    : 'Ocorreu um erro ao exportar o arquivo.'

                this.$bus.$emit('feedback', {
                    message: message,
                    type: 'error',
                })

            })
        },
    },

    computed: {
        paginationParams() {
            let params = this.search ? {
                'search': this.search
            } : {}

            if (this.filters) {
                _.each(this.filters, (value, field) => {
                    params[`filters[${field}]`] = value
                })
            }

            return params
        }
    }
}
</script>

<style>

</style>
